import React from 'react';
import { message } from 'antd';
import { createStore } from 'api/store';
import { Store } from 'api/types/store';
import StoreModal, { StoreModalProps } from '../StoreModal';
import { useSignedUser } from 'api/auth';
import { StoreSource, useAnalytics } from 'analytics';

interface Props extends Omit<StoreModalProps, 'onSubmit'> {
  onSuccess?: (store: Store) => void;
  source?: typeof StoreSource.onboarding | typeof StoreSource.storeList;
}

export default function NewStore({
  onSuccess = () => null,
  source = StoreSource.storeList,
  ...rest
}: Props) {
  const { data: user } = useSignedUser();
  const capture = useAnalytics();

  const handleSubmit = (values) => {
    capture('store_create_submitted', { source });

    return createStore({ owner: user.owner_id, country: 'US', ...values })
      .then((res) => {
        if (res.ok) {
          capture(
            'store_created',
            { source, store_id: res.data?.id },
            { setOnce: { first_store_created_at: new Date().toISOString() } },
          );
          onSuccess(res.data);
          return;
        }
        capture('store_create_failed', {
          source,
          // `errors.fields` is keyed by field name, not indexed.
          reason:
            Object.keys(res.errors.fields ?? {}).join(',') ||
            res.errors.nonFields?.[0] ||
            'unknown',
        });
        message.error(res.errors.fields?.[0]);
      })
      .catch(() => {
        capture('store_create_failed', { source, reason: 'request_failed' });
        message.error('Ups there was an error, please try it later!');
      });
  };

  return <StoreModal mode="new" title="Create Store" onSubmit={handleSubmit} {...rest} />;
}
